import type { Metadata } from 'next';
import de from '@/content/de.json';
import en from '@/content/en.json';
import fr from '@/content/fr.json';
import nl from '@/content/nl.json';

export type Locale = 'nl' | 'en' | 'de' | 'fr';
export const locales: Locale[] = ['nl', 'en', 'de', 'fr'];

const contentMap = {
  nl,
  en,
  de,
  fr,
} as const;

// Allow seoKeywords to be either the full object (nl) or null (other locales)
export type SiteContent = Omit<typeof nl, 'seoKeywords'> & {
  seoKeywords: typeof nl.seoKeywords | null;
};

type SectionId = SiteContent['navigation'][number]['href'];

export function getContent(locale: Locale): SiteContent {
  return contentMap[locale] ?? contentMap.nl;
}

/** Pricing plans as exposed to AI agents through the WebMCP `get_pricing_plans` tool. */
export function getWebMcpPlans(locale: Locale) {
  return getContent(locale).services.items.map(({ price, period, orders, limits, description }) => ({
    price,
    period,
    orders,
    limits,
    description,
  }));
}

export function getSectionHref(section: SectionId): string {
  return `#${section}`;
}

const BRAND_SUFFIX = ' | Winkel Factuur';

/** Google truncates the tab title around here; Ahrefs flags it at the same point. */
const TITLE_LIMIT = 60;

/**
 * The page title as it should actually be rendered.
 *
 * Two things had gone wrong. The root layout carries a `%s | Winkel Factuur`
 * template, and pages that also wrote the suffix themselves ended up with
 * "Inloggen | Winkel Factuur | Winkel Factuur". And the template applied
 * unconditionally, so 143 titles that were a sensible 44-60 characters on their
 * own were pushed past the limit by the brand alone.
 *
 * So: strip whatever suffix is already there, add exactly one, and only when
 * the result still fits. A title long enough to need the room keeps the room -
 * dropping the brand costs less than losing the words that describe the page.
 * Callers pass this through `title: { absolute }`, which bypasses the template.
 */
export function brandedTitle(raw: string): string {
  const base = raw.endsWith(BRAND_SUFFIX) ? raw.slice(0, -BRAND_SUFFIX.length).trim() : raw.trim();

  // A title that already names the brand in its own sentence must not have it
  // appended again: the four press pages read "Pers & Media Kit - Winkel
  // Factuur | Winkel Factuur". Stripping the suffix is not enough, because
  // there the brand is part of the sentence rather than a suffix - and the
  // result stayed under 60 characters, so no length check caught it.
  if (/winkel\s*factuur/i.test(base)) {
    return base;
  }

  return base.length + BRAND_SUFFIX.length <= TITLE_LIMIT ? base + BRAND_SUFFIX : base;
}

/**
 * An `intro` is rendered as rich text on the article itself, so some of them
 * carry an inline link or an <em>. Every other consumer - the meta
 * description, the JSON-LD `description`, the card excerpt, the word count -
 * needs the same sentence as plain text, and markup leaking into those is a
 * bug users see in the Google snippet.
 *
 * Route those consumers through this instead of reading `intro` directly.
 * Entities are decoded rather than stripped, because JSON-LD is unescaped in a
 * single pass: a stray `&amp;` left here would reach Google as `&`.
 */
export function introText(intro?: string): string {
  if (!intro) {
    return '';
  }

  return intro
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

export function createMetadata(locale: Locale): Metadata {
  const content = getContent(locale);
  const { meta } = content;
  const alternateLanguages = locales.reduce<Record<string, string>>((acc, lang) => {
    acc[lang] = `https://winkelfactuur.nl/${lang}/`;
    return acc;
  }, {});

  return {
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    alternates: {
      canonical: meta.canonical,
      languages: alternateLanguages,
    },
    openGraph: {
      type: 'website',
      siteName: 'Winkel Factuur',
      url: meta.canonical,
      title: meta.ogTitle,
      description: meta.ogDescription,
      images: [
        {
          url: 'https://winkelfactuur.nl/images/og-image.png',
          width: 1200,
          height: 630,
          alt: meta.title,
        },
      ],
    },
    twitter: {
      card: 'summary_large_image',
      title: meta.twitterTitle,
      description: meta.twitterDescription,
  images: ['https://winkelfactuur.nl/images/og-image.png'],
    },
    robots: {
      index: true,
      follow: true,
      'max-image-preview': 'large' as const,
      'max-snippet': -1,
      'max-video-preview': -1,
    },
  } satisfies Metadata;
}

export function getFaqStructuredData(locale: Locale) {
  const {
    meta: { faq },
  } = getContent(locale);

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } as const;
}

export function getOrganizationStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://winkelfactuur.nl/#organization',
    name: 'Winkel Factuur',
    legalName: 'For Kıvanç Bilişim Ev Dekorasyon Dış Ticaret Sanayi ve Ticaret Limited Şirketi',
    url: 'https://winkelfactuur.nl',
    logo: {
      '@type': 'ImageObject',
      url: 'https://winkelfactuur.nl/favicon/android-chrome-512x512.png',
      width: 512,
      height: 512,
    },
    image: 'https://winkelfactuur.nl/images/og-image.png',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Pınar Mah. 74229 Sok. Safir Life Sit. A Blok No:16 İç Kapı No:6',
      addressLocality: 'Seyhan / Adana',
      postalCode: '01170',
      addressCountry: 'TR',
    },
    areaServed: ['NL', 'BE', 'DE', 'FR', 'EU'],
    knowsAbout: [
      'e-commerce invoicing',
      'VAT automation',
      'bol.com marketplace integration',
      'UBL e-invoicing',
      'Peppol e-invoicing',
      'Amazon Seller Central invoicing',
      'Shopify invoicing',
      'WooCommerce invoicing',
      'multi-channel e-commerce',
    ],
    sameAs: [
      'https://www.linkedin.com/company/winkelfactuur',
      'https://www.tiktok.com/@winkelfactuur',
      'https://www.facebook.com/winkelfactuur/',
      'https://www.instagram.com/winkelfactuur',
      'https://nl.trustpilot.com/review/winkelfactuur.nl',
    ],
    slogan: 'Automatische facturatie voor e-commerce',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Sales',
      areaServed: 'NL',
      availableLanguage: ['nl', 'en'],
      email: 'sales@winkelfactuur.nl',
    },
  } as const;
}

function getIndividualOffers() {
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const baseOffer = {
    availability: 'https://schema.org/InStock',
    priceValidUntil: priceValidUntilStr,
    priceCurrency: 'EUR',
  };

  return [
    { '@type': 'Offer', name: 'Starter', price: '9', ...baseOffer },
    { '@type': 'Offer', name: 'Growth', price: '15', ...baseOffer },
    { '@type': 'Offer', name: 'Popular', price: '22', ...baseOffer },
    { '@type': 'Offer', name: 'Professional', price: '32', ...baseOffer },
    { '@type': 'Offer', name: 'Business', price: '45', ...baseOffer },
  ];
}

export function getSoftwareApplicationStructuredData(locale: Locale) {
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const descriptions: Record<Locale, string> = {
    nl: 'Automatiseer btw-facturen voor bol.com, Amazon Seller, Shopify en WooCommerce. Directe bol.com API-integratie met 4.9/5 service score.',
    en: 'Automate VAT invoices for bol.com, Amazon Seller, Shopify and WooCommerce. Direct bol.com API integration with 4.9/5 service score.',
    de: 'Automatisieren Sie MwSt-Rechnungen für bol.com, Amazon Seller, Shopify und WooCommerce. Direkte bol.com API-Integration mit 4,9/5 Bewertung.',
    fr: 'Automatisez les factures TVA pour bol.com, Amazon Seller, Shopify et WooCommerce. Intégration API bol.com directe avec une note de 4.9/5.',
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `https://winkelfactuur.nl/${locale}/#software`,
    name: 'Winkel Factuur',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Invoice Automation',
    operatingSystem: 'Web',
    description: descriptions[locale],
    url: `https://winkelfactuur.nl/${locale}/`,
    image: 'https://winkelfactuur.nl/images/og-image.png',
    screenshot: 'https://winkelfactuur.nl/images/screenshots/i1.webp',
    inLanguage: ['nl', 'en', 'de', 'fr'],
    featureList: [
      'Automatic VAT invoice generation',
      'bol.com direct API integration',
      'Amazon Seller Central integration',
      'Shopify and WooCommerce integration',
      'EU OSS tax calculation for 27 countries',
      'UBL e-invoice export (Peppol, NLCIUS)',
      'Automatic credit note generation',
      'Multi-platform support (16+ platforms)',
      'Exact Online, AFAS, Twinfield integration',
      'GDPR-compliant 7-year archiving',
    ],
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: '9',
      highPrice: '45',
      offerCount: 5,
      priceValidUntil: priceValidUntilStr,
      availability: 'https://schema.org/InStock',
      offers: getIndividualOffers(),
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      bestRating: '5',
      worstRating: '1',
      ratingCount: '3',
      reviewCount: '3',
    },
    review: [
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: 'Jan de Vries',
        },
        datePublished: '2024-11-15',
        reviewBody: 'Uitstekende software voor mijn bol.com winkel. Facturen worden automatisch aangemaakt en geüpload.',
      },
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: 'Maria Bakker',
        },
        datePublished: '2024-10-28',
        reviewBody: 'Bespaart me uren werk per week. De integratie met Amazon Seller werkt perfect.',
      },
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: 'Peter Jansen',
        },
        datePublished: '2024-09-12',
        reviewBody: 'Geweldige klantenservice en de software is zeer gebruiksvriendelijk.',
      },
    ],
    author: {
      '@type': 'Organization',
      '@id': 'https://winkelfactuur.nl/#organization',
      name: 'Winkel Factuur',
      url: 'https://winkelfactuur.nl',
    },
    provider: {
      '@type': 'Organization',
      '@id': 'https://winkelfactuur.nl/#organization',
      name: 'For Kıvanç Bilişim Ev Dekorasyon Dış Ticaret Sanayi ve Ticaret Limited Şirketi',
      url: 'https://winkelfactuur.nl',
    },
  } as const;
}


export function getHowToStructuredData(locale: Locale) {
  const baseUrl = `https://winkelfactuur.nl/${locale}/`;

  const howTo1: Record<Locale, { name: string; description: string; steps: { name: string; text: string }[] }> = {
    nl: {
      name: 'Hoe maak je automatische facturen voor je webshop',
      description: 'Leer hoe je automatische facturen instelt voor je webshop met Winkel Factuur. In 5 eenvoudige stappen ben je klaar.',
      steps: [
        { name: 'Meld je aan bij Winkel Factuur', text: 'Maak een account aan op winkelfactuur.nl en kies het pakket dat bij je past.' },
        { name: 'Verbind je e-commerce platform', text: 'Koppel je webshop (Shopify, WooCommerce, bol.com, Amazon Seller, etc.) via de eenvoudige integratie.' },
        { name: 'Configureer je factuursjabloon', text: 'Pas je factuursjabloon aan met je bedrijfsgegevens, logo en huisstijl.' },
        { name: 'Schakel automatische facturering in', text: 'Activeer automatische facturering zodat elke bestelling direct een factuur krijgt.' },
        { name: 'Facturen worden automatisch aangemaakt', text: 'Bij elke nieuwe bestelling wordt automatisch een btw-conforme factuur gegenereerd en verstuurd.' },
      ],
    },
    en: {
      name: 'How to create automatic invoices for your online store',
      description: 'Learn how to set up automatic invoicing for your online store with Winkel Factuur in 5 simple steps.',
      steps: [
        { name: 'Sign up for Winkel Factuur', text: 'Create an account on winkelfactuur.nl and choose the plan that suits your needs.' },
        { name: 'Connect your e-commerce platform', text: 'Link your online store (Shopify, WooCommerce, bol.com, Amazon Seller, etc.) via the easy integration.' },
        { name: 'Configure your invoice template', text: 'Customize your invoice template with your business details, logo, and branding.' },
        { name: 'Enable automatic invoicing', text: 'Activate automatic invoicing so every order receives an invoice instantly.' },
        { name: 'Invoices are automatically generated', text: 'For each new order, a VAT-compliant invoice is automatically generated and sent.' },
      ],
    },
    de: {
      name: 'So erstellen Sie automatische Rechnungen für Ihren Online-Shop',
      description: 'Erfahren Sie, wie Sie mit Winkel Factuur in 5 einfachen Schritten automatische Rechnungen für Ihren Online-Shop einrichten.',
      steps: [
        { name: 'Bei Winkel Factuur anmelden', text: 'Erstellen Sie ein Konto auf winkelfactuur.nl und wählen Sie das passende Paket.' },
        { name: 'E-Commerce-Plattform verbinden', text: 'Verbinden Sie Ihren Online-Shop (Shopify, WooCommerce, bol.com, Amazon Seller, etc.) über die einfache Integration.' },
        { name: 'Rechnungsvorlage konfigurieren', text: 'Passen Sie Ihre Rechnungsvorlage mit Ihren Geschäftsdaten, Logo und Corporate Design an.' },
        { name: 'Automatische Rechnungsstellung aktivieren', text: 'Aktivieren Sie die automatische Rechnungsstellung, damit jede Bestellung sofort eine Rechnung erhält.' },
        { name: 'Rechnungen werden automatisch erstellt', text: 'Bei jeder neuen Bestellung wird automatisch eine MwSt-konforme Rechnung erstellt und versendet.' },
      ],
    },
    fr: {
      name: 'Comment créer des factures automatiques pour votre boutique en ligne',
      description: 'Découvrez comment configurer la facturation automatique pour votre boutique en ligne avec Winkel Factuur en 5 étapes simples.',
      steps: [
        { name: "S'inscrire sur Winkel Factuur", text: 'Créez un compte sur winkelfactuur.nl et choisissez le forfait adapté à vos besoins.' },
        { name: 'Connecter votre plateforme e-commerce', text: 'Reliez votre boutique en ligne (Shopify, WooCommerce, bol.com, Amazon Seller, etc.) via l\'intégration simple.' },
        { name: 'Configurer votre modèle de facture', text: 'Personnalisez votre modèle de facture avec vos coordonnées, logo et identité visuelle.' },
        { name: 'Activer la facturation automatique', text: 'Activez la facturation automatique pour que chaque commande reçoive une facture instantanément.' },
        { name: 'Les factures sont générées automatiquement', text: 'Pour chaque nouvelle commande, une facture conforme à la TVA est automatiquement générée et envoyée.' },
      ],
    },
  };

  const howTo2: Record<Locale, { name: string; description: string; steps: { name: string; text: string }[] }> = {
    nl: {
      name: 'Hoe stel je e-commerce factuurautomatisering in',
      description: 'Stap-voor-stap handleiding om e-commerce factuurautomatisering in te stellen met Winkel Factuur.',
      steps: [
        { name: 'Kies je pakket', text: 'Selecteer het Winkel Factuur pakket dat past bij het aantal bestellingen van je webshop.' },
        { name: 'Voeg je bedrijfsgegevens toe', text: 'Vul je bedrijfsnaam, adres, KVK-nummer en btw-nummer in voor correcte facturen.' },
        { name: 'Verbind je webshop', text: 'Koppel je online winkel via API of plugin aan Winkel Factuur.' },
        { name: 'Pas PDF-sjablonen aan', text: 'Ontwerp je factuur-PDF met je eigen logo, kleuren en lay-out.' },
        { name: 'Configureer belastinginstellingen', text: 'Stel btw-tarieven, vrijstellingen en landspecifieke belastingregels in.' },
        { name: 'Start met automatisch factureren', text: 'Activeer de automatisering en laat Winkel Factuur het werk doen bij elke bestelling.' },
      ],
    },
    en: {
      name: 'How to set up e-commerce invoice automation',
      description: 'Step-by-step guide to setting up e-commerce invoice automation with Winkel Factuur.',
      steps: [
        { name: 'Choose your plan', text: 'Select the Winkel Factuur plan that matches your online store\'s order volume.' },
        { name: 'Add your business details', text: 'Enter your company name, address, registration number, and VAT number for compliant invoices.' },
        { name: 'Connect your online store', text: 'Link your online store to Winkel Factuur via API or plugin.' },
        { name: 'Customize PDF templates', text: 'Design your invoice PDF with your own logo, colors, and layout.' },
        { name: 'Configure tax settings', text: 'Set up VAT rates, exemptions, and country-specific tax rules.' },
        { name: 'Start automated invoicing', text: 'Activate the automation and let Winkel Factuur handle invoicing for every order.' },
      ],
    },
    de: {
      name: 'So richten Sie die E-Commerce-Rechnungsautomatisierung ein',
      description: 'Schritt-für-Schritt-Anleitung zur Einrichtung der E-Commerce-Rechnungsautomatisierung mit Winkel Factuur.',
      steps: [
        { name: 'Paket wählen', text: 'Wählen Sie das Winkel Factuur Paket, das zum Bestellvolumen Ihres Online-Shops passt.' },
        { name: 'Geschäftsdaten hinzufügen', text: 'Geben Sie Ihren Firmennamen, Adresse, Handelsregisternummer und USt-IdNr. für korrekte Rechnungen ein.' },
        { name: 'Online-Shop verbinden', text: 'Verbinden Sie Ihren Online-Shop über API oder Plugin mit Winkel Factuur.' },
        { name: 'PDF-Vorlagen anpassen', text: 'Gestalten Sie Ihre Rechnungs-PDF mit Ihrem eigenen Logo, Farben und Layout.' },
        { name: 'Steuereinstellungen konfigurieren', text: 'Richten Sie MwSt-Sätze, Befreiungen und länderspezifische Steuerregeln ein.' },
        { name: 'Automatische Rechnungsstellung starten', text: 'Aktivieren Sie die Automatisierung und lassen Sie Winkel Factuur die Rechnungsstellung für jede Bestellung übernehmen.' },
      ],
    },
    fr: {
      name: "Comment configurer l'automatisation de la facturation e-commerce",
      description: "Guide étape par étape pour configurer l'automatisation de la facturation e-commerce avec Winkel Factuur.",
      steps: [
        { name: 'Choisir votre forfait', text: 'Sélectionnez le forfait Winkel Factuur adapté au volume de commandes de votre boutique.' },
        { name: 'Ajouter vos coordonnées professionnelles', text: 'Saisissez le nom de votre entreprise, adresse, numéro SIRET et numéro de TVA pour des factures conformes.' },
        { name: 'Connecter votre boutique en ligne', text: 'Reliez votre boutique en ligne à Winkel Factuur via API ou plugin.' },
        { name: 'Personnaliser les modèles PDF', text: 'Concevez votre PDF de facture avec votre propre logo, couleurs et mise en page.' },
        { name: 'Configurer les paramètres fiscaux', text: 'Configurez les taux de TVA, les exonérations et les règles fiscales spécifiques à chaque pays.' },
        { name: 'Démarrer la facturation automatisée', text: "Activez l'automatisation et laissez Winkel Factuur gérer la facturation pour chaque commande." },
      ],
    },
  };

  const buildHowToSchema = (data: { name: string; description: string; steps: { name: string; text: string }[] }, totalTime: string) => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: data.name,
    description: data.description,
    totalTime,
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'EUR',
      value: '5',
    },
    step: data.steps.map((step, index) => ({
      '@type': 'HowToStep',
      name: step.name,
      text: step.text,
      url: `${baseUrl}#step-${index + 1}`,
    })),
  });

  return [
    buildHowToSchema(howTo1[locale], 'PT5M'),
    buildHowToSchema(howTo2[locale], 'PT10M'),
  ];
}

export function getWebSiteStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://winkelfactuur.nl/#website',
    name: 'Winkel Factuur',
    url: 'https://winkelfactuur.nl',
    inLanguage: ['nl', 'en', 'de', 'fr'],
    publisher: {
      '@type': 'Organization',
      '@id': 'https://winkelfactuur.nl/#organization',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://winkelfactuur.nl/nl/search/?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  } as const;
}

/**
 * Enriches structured data for Google Search Console compliance:
 * 1. Ensures offers have priceValidUntil dates
 * 2. Adds default image for SoftwareApplication/WebApplication types
 */
export function enrichStructuredData<T extends Record<string, unknown>>(data: T): T {
  let result = { ...data };
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  if (result.offers && typeof result.offers === 'object' && !Array.isArray(result.offers)) {
    const offers = result.offers as Record<string, unknown>;
    if (!offers.priceValidUntil) {
      result = { ...result, offers: { ...offers, priceValidUntil } };
    }
  }

  const type = result['@type'] as string;
  if (['SoftwareApplication', 'WebApplication'].includes(type) && !result.image) {
    result = { ...result, image: 'https://winkelfactuur.nl/images/og-image.png' };
  }

  return result as T;
}
